/**
 * InvoiceFormDialog — New / Edit / Copy for Sales Control › Invoice.
 * Converted 1:1 from Access `frmCtrlSalesQryInvoiceAdd`: 3-col form (Access Left
 * 3720 / 9744 / 15708 → col 1/2/3) grouped into Access gutter sections
 * (Contract Info · Invoice Info · Final Price · Provisional Price · Custom
 * Invoice · Customer Invoice · Receipt · Payment). Combos = Autocomplete (store
 * code). Number fields Standard (2dp, right-aligned), dates DD-MMM-YYYY.
 *
 * Access btnSave validation (errCheck): contract_ref required (5); exactly one of
 * No.Inv / No.VAT (2 both empty, 3 both filled); invoice_date required (4);
 * duplicate customs_inv_no (1) — handled by /access-suggest on the No.Inv blur.
 *
 * Cascades (Access *_AfterUpdate):
 *   inv_no_index → customs_inv_no="INV"+idx, customer_invoice_number="INV"+idx+".F", clear No.VAT
 *   vat_no_index → customs_inv_no="VAT"+idx, customer_invoice_number="VAT"+idx+".F", clear No.Inv
 *   contract_ref → snapshot (fx_auto / price_unit / contract_type / price / final_price_usd / grade / goods_description)
 *   fp_fx        → final_price_vnd = final_price_usd × 1000 / fp_fx
 *
 * Edit disables contract_ref / No.Inv / No.VAT (Access sets .Enabled=False);
 * Copy hydrates from the source row but takes a fresh suggested index, fields enabled.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type InvLookupItem,
  type SaleInvoiceWrite,
  useCreateSaleInvoiceMutation,
  useUpdateSaleInvoiceMutation,
  useLazyGetSaleInvoiceFormQuery,
  useLazyGetInvContractSnapshotQuery,
  useLazySuggestInvoiceNoQuery,
  useLazyCheckInvoiceNoQuery,
  useInvLookupContractsQuery,
  useInvLookupBanksQuery,
} from '../../store/api/saleInvoiceApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

type Mode = 'new' | 'edit' | 'copy'

interface Props {
  open: boolean
  mode: Mode
  invoiceId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  contractRef: string
  toBank: string
  invNoIndex: string
  vatNoIndex: string
  customsInvNo: string
  customerInvoiceNumber: string
  invoiceDate: string
  blDate: string
  commercialInvDate: string
  goodsDescription: string
  othersDescription: string
  fpFx: string
  ppClosingPrice: string
  ppFx: string
  customerInvAdjust: string
  receiptWh: string
  customersCreditNo: string
  reclaim: string
  paidDetails: string
  // readonly display (contract snapshot + carried-through persisted values)
  noBag: string
  contractType: string
  contractFxAuto: string
  contractPriceUnit: string
  contractPrice: string
  contractGrade: string
  finalPriceUsd: string
  finalPriceVnd: string
  customerInvGw: string
  customerInvNw: string
  customInvGw: string
  customInvNw: string
  receiptGw: string
  receiptNw: string
  toBeReleased: string
  toBePaid: string
}

const EMPTY: Draft = {
  contractRef: '', toBank: '', invNoIndex: '', vatNoIndex: '', customsInvNo: '',
  customerInvoiceNumber: '', invoiceDate: '', blDate: '', commercialInvDate: '',
  goodsDescription: '', othersDescription: '', fpFx: '', ppClosingPrice: '', ppFx: '',
  customerInvAdjust: '', receiptWh: '', customersCreditNo: '', reclaim: '', paidDetails: '',
  noBag: '', contractType: '', contractFxAuto: '', contractPriceUnit: '', contractPrice: '',
  contractGrade: '', finalPriceUsd: '', finalPriceVnd: '', customerInvGw: '', customerInvNw: '',
  customInvGw: '', customInvNw: '', receiptGw: '', receiptNw: '', toBeReleased: '', toBePaid: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const sectionTitleSx: SxProps<Theme> = {
  fontWeight: 700, fontSize: '0.8125rem', color: 'primary.main',
  mt: 1.5, mb: 0.75, pb: 0.25, borderBottom: '1px solid', borderColor: 'divider',
}
const grid3Sx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

function withCurrent(options: InvLookupItem[], value: string, label?: string): InvLookupItem | null {
  if (!value) return null
  return options.find((o) => o.value === value) ?? { value, label: label || value }
}

function Combo(props: {
  label: string
  value: string
  options: InvLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  error?: boolean
  onInputChange?: (q: string) => void
  onPick: (value: string) => void
}) {
  const { label, value, options, loading, required, disabled, error, onInputChange, onPick } = props
  const sel = withCurrent(options, value)
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '')}
      onInputChange={onInputChange ? (_e, v, reason) => { if (reason === 'input') onInputChange(v) } : undefined}
      getOptionLabel={(o) => (!o ? '' : o.value === o.label ? String(o.label ?? '') : `${o.value} — ${o.label}`)}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={onInputChange ? (x) => x : undefined}
      renderInput={(params) => <TextField {...params} label={required ? `${label} *` : label} error={error} />}
    />
  )
}

export default function InvoiceFormDialog({ open, mode, invoiceId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const [loadForm] = useLazyGetSaleInvoiceFormQuery()
  const [loadSnapshot] = useLazyGetInvContractSnapshotQuery()
  const [suggest] = useLazySuggestInvoiceNoQuery()
  const [checkInv] = useLazyCheckInvoiceNoQuery()
  const [createInv, { isLoading: creating }] = useCreateSaleInvoiceMutation()
  const [updateInv, { isLoading: updating }] = useUpdateSaleInvoiceMutation()
  const saving = creating || updating

  const [contractQ, setContractQ] = useState('')
  const skip = { skip: !open }
  const contracts = useInvLookupContractsQuery(contractQ || undefined, skip)
  const banks = useInvLookupBanksQuery(undefined, skip)

  const readonly = mode === 'edit'

  // ── contract snapshot → readonly derived fields (contract_ref_AfterUpdate) ──
  const applySnapshot = async (contractRef: string, fpFx: string) => {
    if (!contractRef) return
    const snap = await loadSnapshot(contractRef).unwrap().catch(() => null)
    if (!snap) return
    const usd = snap.finalPriceUsd ?? 0
    const fx = parseNum(fpFx)
    const vnd = fx ? (usd * 1000) / fx : 0
    setDraft((p) => ({
      ...p,
      contractFxAuto: fmtStd(snap.contractFxAuto),
      contractPriceUnit: snap.contractPriceUnit ?? '',
      contractType: snap.contractType ?? '',
      contractPrice: fmtStd(snap.contractPrice),
      finalPriceUsd: fmtStd(usd),
      contractGrade: snap.contractGrade ?? '',
      goodsDescription: snap.goodsDescription || p.goodsDescription,
      finalPriceVnd: fmtStd(vnd),
    }))
  }

  // ── Hydrate on open (deps: [open, mode, invoiceId] only) ──────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    void (async () => {
      if (mode === 'new') {
        const today = dayjs().format('YYYY-MM-DD')
        const idx = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        const i = idx ? String(idx.index) : ''
        setDraft({
          ...EMPTY,
          invoiceDate: today,
          invNoIndex: i,
          customsInvNo: i ? `INV${i}` : '',
          customerInvoiceNumber: i ? `INV${i}.F` : '',
        })
        return
      }
      if (!invoiceId) return
      const f = await loadForm(invoiceId).unwrap().catch(() => null)
      if (cancelled || !f) return
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      const isCopy = mode === 'copy'
      // Copy: fresh suggested inv index; Edit: keep the stored index.
      let invNoIndex = g('invoiceNo')
      let vatNoIndex = g('vatNo')
      let customsInvNo = g('customsInvNo')
      let customerInvoiceNumber = g('customerInvoiceNumber')
      if (isCopy) {
        const idx = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        const i = idx ? String(idx.index) : ''
        invNoIndex = i
        vatNoIndex = ''
        customsInvNo = i ? `INV${i}` : ''
        customerInvoiceNumber = i ? `INV${i}.F` : ''
      }
      const fpFx = fmtStd(g('fpFx'))
      setDraft({
        ...EMPTY,
        contractRef: g('contractRef'), toBank: g('toBank'),
        invNoIndex, vatNoIndex, customsInvNo, customerInvoiceNumber,
        invoiceDate: isCopy ? dayjs().format('YYYY-MM-DD') : g('invoiceDate').slice(0, 10),
        blDate: g('blDate').slice(0, 10), commercialInvDate: g('commercialInvDate').slice(0, 10),
        goodsDescription: g('goodsDescription'), othersDescription: g('othersDescription'),
        fpFx, ppClosingPrice: fmtStd(g('ppClosingPrice')), ppFx: fmtStd(g('ppFx')),
        customerInvAdjust: fmtStd(g('customerInvAdjust')), receiptWh: g('receiptWh'),
        customersCreditNo: g('customersCreditNo'), reclaim: g('reclaim'), paidDetails: g('paidDetails'),
        noBag: fmtStd(g('noBag')),
        customerInvGw: fmtStd(g('customerInvGw')), customerInvNw: fmtStd(g('customerInvNw')),
        customInvGw: fmtStd(g('customInvGw')), customInvNw: fmtStd(g('customInvNw')),
        receiptGw: fmtStd(g('receiptGw')), receiptNw: fmtStd(g('receiptNw')),
        toBeReleased: fmtStd(g('toBeReleased')), toBePaid: fmtStd(g('toBePaid')),
      })
      // Recompute derived contract fields from the hydrated contract.
      void applySnapshot(g('contractRef'), fpFx)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, invoiceId])

  // Dismiss regen alert when the user edits the index away from the suggestion.
  useEffect(() => {
    if (regenNotice && draft.invNoIndex !== regenNotice.to) setRegenNotice(null)
  }, [draft.invNoIndex, regenNotice])

  // ── Cascades ──────────────────────────────────────────────────────────────
  const onInvIdxChange = (idx: string) => {
    setRegenNotice(null)
    setDraft((p) => ({
      ...p,
      invNoIndex: idx,
      vatNoIndex: idx ? '' : p.vatNoIndex,
      customsInvNo: idx ? `INV${idx}` : p.vatNoIndex ? `VAT${p.vatNoIndex}` : '',
      customerInvoiceNumber: idx ? `INV${idx}.F` : p.vatNoIndex ? `VAT${p.vatNoIndex}.F` : '',
    }))
  }
  const onVatIdxChange = (idx: string) => {
    setDraft((p) => ({
      ...p,
      vatNoIndex: idx,
      invNoIndex: idx ? '' : p.invNoIndex,
      customsInvNo: idx ? `VAT${idx}` : p.invNoIndex ? `INV${p.invNoIndex}` : '',
      customerInvoiceNumber: idx ? `VAT${idx}.F` : p.invNoIndex ? `INV${p.invNoIndex}.F` : '',
    }))
  }
  const onContractPick = (v: string) => { set('contractRef', v); void applySnapshot(v, draft.fpFx) }
  const onFpFxBlur = () => {
    const formatted = fmtStd(draft.fpFx)
    const usd = parseNum(draft.finalPriceUsd) ?? 0
    const fx = parseNum(formatted)
    setDraft((p) => ({ ...p, fpFx: formatted, finalPriceVnd: fmtStd(fx ? (usd * 1000) / fx : 0) }))
  }

  // ── Race-safe duplicate check on No.Inv blur (/access-suggest) ─────────────
  const handleInvBlur = async () => {
    if (readonly) return
    const val = draft.invNoIndex.trim()
    if (!val) return
    const n = parseInt(val, 10)
    if (!Number.isFinite(n)) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkInv({ index: n, excludeId: undefined }).unwrap()
      if (!check.exists) return
      const next = await suggest().unwrap()
      const newVal = String(next.index || '').trim()
      if (newVal && newVal !== val) {
        onInvIdxChange(newVal)
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE re-checks on save; do not poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const contractEmpty = !draft.contractRef.trim()
  const dateEmpty = !draft.invoiceDate.trim()
  const invSet = !!draft.invNoIndex.trim()
  const vatSet = !!draft.vatNoIndex.trim()
  const exactlyOne = invSet !== vatSet
  const canSave = canEdit && !contractEmpty && !dateEmpty && exactlyOne && !saving && !precheckBusy

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (!canSave) return
    const payload: SaleInvoiceWrite = {
      contractRef: draft.contractRef || null,
      toBank: draft.toBank || null,
      invoiceNo: draft.invNoIndex || null,
      vatNo: draft.vatNoIndex || null,
      customsInvNo: draft.customsInvNo || null,
      customerInvoiceNumber: draft.customerInvoiceNumber || null,
      invoiceDate: draft.invoiceDate || null,
      blDate: draft.blDate || null,
      commercialInvDate: draft.commercialInvDate || null,
      goodsDescription: draft.goodsDescription || null,
      othersDescription: draft.othersDescription || null,
      fpFx: parseNum(draft.fpFx),
      ppClosingPrice: parseNum(draft.ppClosingPrice),
      ppFx: parseNum(draft.ppFx),
      customerInvAdjust: parseNum(draft.customerInvAdjust),
      receiptWh: draft.receiptWh || null,
      customersCreditNo: draft.customersCreditNo || null,
      reclaim: draft.reclaim || null,
      paidDetails: draft.paidDetails || null,
      customerInvGw: parseNum(draft.customerInvGw),
      customerInvNw: parseNum(draft.customerInvNw),
      customInvGw: parseNum(draft.customInvGw),
      customInvNw: parseNum(draft.customInvNw),
      receiptGw: parseNum(draft.receiptGw),
      receiptNw: parseNum(draft.receiptNw),
      toBeReleased: parseNum(draft.toBeReleased),
      toBePaid: parseNum(draft.toBePaid),
    }
    try {
      if (mode === 'edit' && invoiceId) {
        await updateInv({ id: invoiceId, body: payload }).unwrap()
        snack.success(`Invoice ${draft.customsInvNo} updated`)
      } else {
        await createInv(payload).unwrap()
        snack.success(`Invoice ${draft.customsInvNo} added`)
      }
      onSaved()
      onClose()
    } catch (err) {
      const msg = getErrorMessage(err, 'Save invoice failed')
      // Duplicate slipped past the blur pre-check → suggest a fresh index inline.
      if (mode !== 'edit' && /exist|duplicate/i.test(msg)) {
        try {
          const next = await suggest().unwrap()
          const newVal = String(next.index || '').trim()
          const oldVal = draft.invNoIndex.trim()
          if (newVal && newVal !== oldVal) {
            onInvIdxChange(newVal)
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch { /* fallthrough */ }
      }
      snack.error(msg)
    }
  }

  const title = mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{ textField: { size: SM, fullWidth: true, required, error: required && !(draft[key] as string), InputLabelProps: { shrink: true } } }}
    />
  )
  const numField = (key: keyof Draft, label: string, extraBlur?: () => void) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => (extraBlur ? extraBlur() : set(key, fmtStd(draft[key] as string) as Draft[typeof key]))}
      inputProps={numProps}
    />
  )
  const textField = (key: keyof Draft, label: string, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={numeric ? { style: { textAlign: 'right' } } : undefined} />
  )
  const cell = (col: number | string, row: number, node: React.ReactNode) => (
    <Box sx={{ gridColumn: { md: col }, gridRow: { md: row } }}>{node}</Box>
  )

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>{title} — Invoice</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit invoices.</Alert>}
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            No. Inv <b>{regenNotice.from}</b> was just created — suggested new code <b>{regenNotice.to}</b>.
          </Alert>
        )}

        {/* ── Contract Info (Access T=372–1044) ── */}
        <Typography sx={sectionTitleSx}>Contract Info</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <Combo label="Contract Ref" value={draft.contractRef} required disabled={readonly} error={contractEmpty}
              options={contracts.data?.items ?? []} loading={contracts.isFetching} onInputChange={setContractQ}
              onPick={onContractPick} />
          ))}
          {cell(2, 1, dateField('blDate', 'B/L Date'))}
          {cell(3, 1, dateField('commercialInvDate', 'Commercial Inv Date'))}
          {cell(1, 2, roField('contractType', 'Contract Type'))}
          {cell(2, 2, roField('contractFxAuto', 'FX Auto', true))}
          {cell(3, 2, roField('contractPriceUnit', 'Price Unit'))}
          {cell(1, 3, roField('contractPrice', 'Contract Price', true))}
          {cell(2, 3, roField('contractGrade', 'Grade'))}
        </Box>

        {/* ── Invoice Info (Access T=1380–2964) ── */}
        <Typography sx={sectionTitleSx}>Invoice Info</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, (
            <TextField label="No. Inv" size={SM} fullWidth disabled={readonly} value={draft.invNoIndex}
              onChange={(e) => onInvIdxChange(e.target.value)} onBlur={() => void handleInvBlur()}
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              helperText={
                readonly ? 'No. Inv cannot be changed after creation.'
                  : precheckBusy ? 'Verifying No. Inv…'
                  : mode === 'copy' ? 'Suggested below; edit if needed.'
                  : 'Suggested code below; edit if needed.'
              } />
          ))}
          {cell(2, 1, (
            <TextField label="No. VAT" size={SM} fullWidth disabled={readonly} value={draft.vatNoIndex}
              onChange={(e) => onVatIdxChange(e.target.value)}
              inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              error={invSet && vatSet} helperText={invSet && vatSet ? 'Enter only No. Inv or No. VAT.' : undefined} />
          ))}
          {cell(1, 2, roField('customsInvNo', 'Customer Inv No.'))}
          {cell(2, 2, roField('customerInvoiceNumber', 'Custom Inv No.'))}
          {cell(3, 2, (
            <Combo label="To Bank" value={draft.toBank} options={banks.data?.items ?? []} loading={banks.isFetching}
              onPick={(v) => set('toBank', v)} />
          ))}
          {cell(1, 3, dateField('invoiceDate', 'Invoice Date', true))}
          {cell(2, 3, roField('noBag', 'No. Bag', true))}
          {cell('1 / -1', 4, textField('goodsDescription', 'Goods Description', { multiline: true, minRows: 2 }))}
          {cell('1 / -1', 5, textField('othersDescription', 'Others', { multiline: true, minRows: 2 }))}
        </Box>

        {/* ── Final Price (Access T=6444) ── */}
        <Typography sx={sectionTitleSx}>Final Price</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, numField('fpFx', 'FX', onFpFxBlur))}
          {cell(2, 1, roField('finalPriceUsd', 'Final Price USD', true))}
          {cell(3, 1, roField('finalPriceVnd', 'Final Price VND', true))}
        </Box>

        {/* ── Provisional Price (Access T=6780) ── */}
        <Typography sx={sectionTitleSx}>Provisional Price</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, numField('ppClosingPrice', 'Closing Price'))}
          {cell(2, 1, numField('ppFx', 'FX'))}
        </Box>

        {/* ── Custom Invoice (Access T=7116) ── */}
        <Typography sx={sectionTitleSx}>Custom Invoice</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('customInvGw', 'Gross Weight', true))}
          {cell(2, 1, roField('customInvNw', 'Net Weight', true))}
        </Box>

        {/* ── Customer Invoice (Access T=7452) ── */}
        <Typography sx={sectionTitleSx}>Customer Invoice</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('customerInvGw', 'Gross Weight', true))}
          {cell(2, 1, roField('customerInvNw', 'Net Weight', true))}
          {cell(3, 1, numField('customerInvAdjust', 'Adjust'))}
        </Box>

        {/* ── Receipt (Access T=7788) ── */}
        <Typography sx={sectionTitleSx}>Receipt</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, textField('receiptWh', 'Warehouse'))}
          {cell(2, 1, roField('receiptGw', 'Gross Weight', true))}
          {cell(3, 1, roField('receiptNw', 'Net Weight', true))}
        </Box>

        {/* ── Payment (Access T=8124–8796) ── */}
        <Typography sx={sectionTitleSx}>Payment</Typography>
        <Box sx={grid3Sx}>
          {cell(1, 1, roField('toBeReleased', 'To be Released', true))}
          {cell(2, 1, roField('toBePaid', 'To be Paid', true))}
          {cell(1, 2, textField('customersCreditNo', "Customer's Credit No."))}
          {cell(2, 2, textField('reclaim', 'Reclaim'))}
          {cell('1 / -1', 3, textField('paidDetails', 'Paid Details', { multiline: true, minRows: 2 }))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
